import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPen,
  faPlus,
  faStar,
  faTrashCan,
  faPenNib,
} from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import {
  useAgentTones,
  useCreateAgentTone,
  useDeleteAgentTone,
  useSetDefaultAgentTone,
  useUpdateAgentTone,
} from '../../hooks/useAgentTones';
import type {
  AgentTone,
  CreateAgentToneRequest,
} from '../../api/agent-tones.api';
import styles from './TonesCard.module.css';
import type_ from './settingsType.module.css';

/** Server-side cap; the Add button disables at the limit rather than failing. */
const MAX_TONES = 4;

function ToneEditor({
  opened,
  tone,
  saving,
  onClose,
  onSave,
}: {
  readonly opened: boolean;
  readonly tone: AgentTone | null;
  readonly saving: boolean;
  readonly onClose: () => void;
  readonly onSave: (data: CreateAgentToneRequest) => void;
}) {
  const [name, setName] = useState(tone?.name ?? '');
  const [content, setContent] = useState(tone?.content ?? '');

  // Remount per tone so the fields reset without an effect.
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={tone ? 'Edit tone' : 'New tone'}
      centered
      // Nested inside the settings modal, which already sits above the widget.
      zIndex={1500}
      styles={{ inner: { left: 0, right: 0 } }}
    >
      <Stack gap="md">
        <TextInput
          label="Name"
          placeholder="Formal"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          maxLength={60}
        />
        <Textarea
          label="Instructions"
          description="Tell Moo how to write when this tone is selected."
          placeholder="Be concise and avoid jargon. Prefer short sentences."
          value={content}
          onChange={(e) => setContent(e.currentTarget.value)}
          autosize
          minRows={4}
          maxRows={10}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={saving}
            disabled={!name.trim() || !content.trim()}
            onClick={() =>
              onSave({
                name: name.trim(),
                content: content.trim(),
                isDefault: tone?.isDefault ?? false,
              })
            }
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Tone management, moved here from My Profile so tones are defined where they
 * are used. Ported from MUI to `@cowbell/ui` — a straight lift would have put
 * two visual languages inside one dialog.
 *
 * This card defines tones and picks the default. Selecting a tone for the
 * current conversation stays on the composer.
 */
export function TonesCard() {
  const [editing, setEditing] = useState<AgentTone | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const { data: tones = [], isLoading, error } = useAgentTones();
  const createMutation = useCreateAgentTone();
  const updateMutation = useUpdateAgentTone();
  const deleteMutation = useDeleteAgentTone();
  const setDefaultMutation = useSetDefaultAgentTone();

  const isMutating =
    createMutation.isLoading ||
    updateMutation.isLoading ||
    deleteMutation.isLoading ||
    setDefaultMutation.isLoading;

  const mutationError =
    (createMutation.error as Error)?.message ??
    (updateMutation.error as Error)?.message ??
    (deleteMutation.error as Error)?.message ??
    (setDefaultMutation.error as Error)?.message ??
    null;

  const handleSave = async (data: CreateAgentToneRequest) => {
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
    setEditing(null);
    setEditorOpen(false);
  };

  const atLimit = tones.length >= MAX_TONES;

  return (
    <Paper
      withBorder
      // Set here rather than in CSS: Mantine's Paper applies its own
      // background, which wins over the stylesheet class.
      bg="var(--mantine-color-default-hover)"
      className={styles.card}
    >
      <Group
        justify="space-between"
        align="flex-start"
        wrap="nowrap"
        className={styles.header}
      >
        <div className={styles.headerCopy}>
          <div className={styles.titleRow}>
            <FontAwesomeIcon icon={faPenNib} className={styles.titleIcon} />
            <Text className={type_.cardTitle}>Tones</Text>
          </div>
          <Text className={type_.subtitle}>
            Define how Moo writes back to you. Pick one per conversation from
            the composer, or set a default here. Up to {MAX_TONES}.
          </Text>
        </div>
        <Tooltip
          label={`You can have up to ${MAX_TONES} tones`}
          disabled={!atLimit}
        >
          <Button
            size="sm"
            className={styles.addButton}
            leftSection={<FontAwesomeIcon icon={faPlus} />}
            disabled={isMutating || atLimit}
            onClick={() => {
              setEditing(null);
              setEditorOpen(true);
            }}
          >
            Add tone
          </Button>
        </Tooltip>
      </Group>

      {(error || mutationError) && (
        <Alert color="red" className={styles.alert}>
          {(error as Error)?.message ?? mutationError}
        </Alert>
      )}

      {isLoading && (
        <div className={styles.centered} data-testid="tones-loading">
          <Loader size="sm" />
        </div>
      )}

      {!isLoading && tones.length === 0 && (
        <Text className={`${type_.subtitle} ${styles.empty}`}>
          No tones yet. Add one to change how Moo writes.
        </Text>
      )}

      {!isLoading && tones.length > 0 && (
        <Stack gap="xs" className={styles.list}>
          {tones.map((tone) => (
            <div key={tone.id} className={styles.row}>
              <div className={styles.rowBody}>
                <Group gap="xs" wrap="nowrap">
                  <Text className={type_.rowTitle}>{tone.name}</Text>
                  {tone.isDefault && (
                    <Badge size="xs" variant="light">
                      Default
                    </Badge>
                  )}
                </Group>
                <Text className={type_.rowMeta} lineClamp={2}>
                  {tone.content}
                </Text>
              </div>
              <Group gap={4} wrap="nowrap">
                <Tooltip
                  label={tone.isDefault ? 'Default tone' : 'Set as default'}
                >
                  <Button
                    variant="subtle"
                    color="gray"
                    size="compact-sm"
                    className={
                      tone.isDefault
                        ? `${styles.rowAction} ${styles.rowActionActive}`
                        : styles.rowAction
                    }
                    aria-label={
                      tone.isDefault
                        ? `${tone.name} is the default tone`
                        : `Set ${tone.name} as default`
                    }
                    disabled={isMutating || tone.isDefault}
                    onClick={() => setDefaultMutation.mutate(tone.id)}
                  >
                    <FontAwesomeIcon
                      icon={tone.isDefault ? faStarSolid : faStar}
                    />
                  </Button>
                </Tooltip>
                <Button
                  variant="subtle"
                  color="gray"
                  size="compact-sm"
                  className={styles.rowAction}
                  aria-label={`Edit ${tone.name}`}
                  disabled={isMutating}
                  onClick={() => {
                    setEditing(tone);
                    setEditorOpen(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPen} />
                </Button>
                <Button
                  variant="subtle"
                  color="gray"
                  size="compact-sm"
                  className={`${styles.rowAction} ${styles.rowActionDanger}`}
                  aria-label={`Delete ${tone.name}`}
                  disabled={isMutating}
                  onClick={() => deleteMutation.mutate(tone.id)}
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </Button>
              </Group>
            </div>
          ))}
        </Stack>
      )}

      {editorOpen && (
        <ToneEditor
          key={editing?.id ?? 'new'}
          opened={editorOpen}
          tone={editing}
          saving={createMutation.isLoading || updateMutation.isLoading}
          onClose={() => {
            setEditorOpen(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}
    </Paper>
  );
}
